import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import { AuthProvider, useAuth } from "react-oidc-context";
import { WebStorageStateStore } from "oidc-client-ts";
import { LogIn, ShieldAlert } from "lucide-react";
import { api, configureApiAuth } from "@/lib/api";
import { Button } from "@/components/ui/button";

const AppAuthContext = createContext(null);
const AUTH_MODE = (process.env.REACT_APP_AUTH_MODE || "disabled").toLowerCase();

class MemoryStore {
  constructor() {
    this.values = new Map();
  }
  get length() { return this.values.size; }
  clear() { this.values.clear(); }
  getItem(key) { return this.values.has(key) ? this.values.get(key) : null; }
  key(index) { return [...this.values.keys()][index] ?? null; }
  removeItem(key) { this.values.delete(key); }
  setItem(key, value) { this.values.set(key, value); }
}

const userStore = new WebStorageStateStore({ store: new MemoryStore() });
const GOOGLE_IDENTITY_SCRIPT = "https://accounts.google.com/gsi/client";

let googleIdentityScriptPromise;

function loadGoogleIdentityScript() {
  if (window.google?.accounts?.id) return Promise.resolve(window.google);
  if (googleIdentityScriptPromise) return googleIdentityScriptPromise;
  googleIdentityScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GOOGLE_IDENTITY_SCRIPT}"]`);
    const script = existing || document.createElement("script");
    const onLoad = () => {
      if (window.google?.accounts?.id) resolve(window.google);
      else reject(new Error("Google Identity Services did not initialize."));
    };
    const onError = () => reject(new Error("Google Identity Services could not be loaded."));
    script.addEventListener("load", onLoad, { once: true });
    script.addEventListener("error", onError, { once: true });
    if (!existing) {
      script.src = GOOGLE_IDENTITY_SCRIPT;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });
  return googleIdentityScriptPromise;
}

function SignInScreen({ error, login }) {
  return (
    <div className="min-h-screen bg-[#F3F6F7] text-slate-900 grid place-items-center p-6">
      <div className="panel p-7 w-full max-w-md">
        <div className="font-heading text-2xl font-black tracking-tight">Portcall HQ</div>
        <p className="text-sm text-slate-400 mt-2">
          Sign in with your organization account to continue.
        </p>
        {error && (
          <div className="mt-4 sev-critical p-3 text-sm flex gap-2">
            <ShieldAlert size={17} className="shrink-0" />
            <span>{error}</span>
          </div>
        )}
        <Button onClick={login} className="mt-5 w-full bg-sky-600 hover:bg-sky-500 rounded-none">
          <LogIn size={15} className="mr-2" /> Sign in
        </Button>
      </div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-[#F3F6F7] text-slate-500 grid place-items-center font-mono text-xs">
      Establishing secure session…
    </div>
  );
}

function GoogleIdentityBridge({ children, clientId }) {
  const buttonRef = useRef(null);
  const [token, setToken] = useState("");
  const [principal, setPrincipal] = useState(null);
  const [error, setError] = useState("");
  const [googleReady, setGoogleReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadGoogleIdentityScript()
      .then(google => {
        if (cancelled) return;
        google.accounts.id.initialize({
          client_id: clientId,
          auto_select: false,
          cancel_on_tap_outside: true,
          callback: response => {
            if (!response?.credential) {
              setError("Google did not return an identity token.");
              return;
            }
            setError("");
            setPrincipal(null);
            setToken(response.credential);
          },
        });
        setGoogleReady(true);
      })
      .catch(loadError => {
        if (!cancelled) setError(loadError.message);
      });
    return () => { cancelled = true; };
  }, [clientId]);

  useEffect(() => {
    if (!googleReady || token || !buttonRef.current) return;
    buttonRef.current.replaceChildren();
    window.google.accounts.id.renderButton(buttonRef.current, {
      type: "standard",
      theme: "filled_blue",
      size: "large",
      text: "signin_with",
      shape: "rectangular",
      width: 340,
    });
  }, [googleReady, token, error]);

  const login = useCallback(() => {
    setError("");
    window.google?.accounts?.id?.prompt();
  }, []);

  const logout = useCallback(async () => {
    window.google?.accounts?.id?.disableAutoSelect();
    setPrincipal(null);
    setToken("");
  }, []);

  useEffect(() => {
    configureApiAuth({
      getAccessToken: async () => token || null,
      onUnauthorized: () => {
        setPrincipal(null);
        setToken("");
        setError("Your Google session expired. Sign in again.");
      },
    });
    return () => configureApiAuth();
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setPrincipal(null);
      return () => { cancelled = true; };
    }
    setError("");
    api.me()
      .then(value => { if (!cancelled) setPrincipal(value); })
      .catch(requestError => {
        if (!cancelled) {
          setPrincipal(null);
          setToken("");
          setError(requestError.response?.data?.detail || "Your application role could not be verified.");
        }
      });
    return () => { cancelled = true; };
  }, [token]);

  const value = useMemo(() => ({
    isAuthenticated: Boolean(token && principal),
    isLoading: Boolean(token && !principal && !error),
    principal,
    capabilities: principal?.capabilities || [],
    hasCapability: capability => Boolean(principal?.capabilities?.includes(capability)),
    getAccessToken: async () => token || null,
    login,
    logout,
  }), [token, principal, error, login, logout]);

  if (value.isLoading) return <LoadingScreen />;
  if (!value.isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050B14] text-slate-100 grid place-items-center p-6">
        <div className="panel p-7 w-full max-w-md">
          <div className="font-heading text-2xl font-black tracking-tight">Portcall HQ</div>
          <p className="text-sm text-slate-400 mt-2">
            Sign in with your organization account to continue.
          </p>
          {error && (
            <div className="mt-4 sev-critical p-3 text-sm flex gap-2">
              <ShieldAlert size={17} className="shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <div ref={buttonRef} className="mt-5 min-h-10 flex justify-center" />
          {!googleReady && !error && (
            <div className="mt-3 text-center text-xs text-slate-500">Loading Google sign-in…</div>
          )}
        </div>
      </div>
    );
  }
  return <AppAuthContext.Provider value={value}>{children}</AppAuthContext.Provider>;
}

function OidcBridge({ children }) {
  const auth = useAuth();
  const [principal, setPrincipal] = useState(null);
  const [profileError, setProfileError] = useState("");
  const token = process.env.REACT_APP_OIDC_TOKEN_SOURCE === "id_token"
    ? auth.user?.id_token
    : auth.user?.access_token;

  const login = useCallback(() => auth.signinRedirect({
    state: { returnTo: `${window.location.pathname}${window.location.search}` },
  }), [auth]);

  const logout = useCallback(async () => {
    setPrincipal(null);
    await auth.removeUser();
    try {
      await auth.signoutRedirect();
    } catch {
      // Google discovery does not advertise an OIDC end-session endpoint.
      window.location.assign("/");
    }
  }, [auth]);

  useEffect(() => {
    configureApiAuth({
      getAccessToken: async () => token || null,
      onUnauthorized: () => {
        if (!auth.isLoading) login();
      },
    });
    return () => configureApiAuth();
  }, [token, auth.isLoading, login]);

  useEffect(() => {
    let cancelled = false;
    if (!auth.isAuthenticated || !token) {
      setPrincipal(null);
      return () => { cancelled = true; };
    }
    setProfileError("");
    api.me()
      .then(value => { if (!cancelled) setPrincipal(value); })
      .catch(error => {
        if (!cancelled) {
          setProfileError(error.response?.data?.detail || "Your application role could not be verified.");
        }
      });
    return () => { cancelled = true; };
  }, [auth.isAuthenticated, token]);

  const value = useMemo(() => ({
    isAuthenticated: Boolean(auth.isAuthenticated && principal),
    isLoading: auth.isLoading || (auth.isAuthenticated && !principal && !profileError),
    principal,
    capabilities: principal?.capabilities || [],
    hasCapability: capability => Boolean(principal?.capabilities?.includes(capability)),
    getAccessToken: async () => token || null,
    login,
    logout,
  }), [auth.isAuthenticated, auth.isLoading, token, principal, profileError, login, logout]);

  if (auth.isLoading || value.isLoading) return <LoadingScreen />;
  if (auth.error || profileError) {
    return <SignInScreen error={profileError || auth.error.message} login={login} />;
  }
  if (!value.isAuthenticated) return <SignInScreen login={login} />;
  return <AppAuthContext.Provider value={value}>{children}</AppAuthContext.Provider>;
}

function DevelopmentBridge({ children }) {
  const [principal, setPrincipal] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    configureApiAuth();
    let cancelled = false;
    api.me()
      .then(value => { if (!cancelled) setPrincipal(value); })
      .catch(() => { if (!cancelled) setError("The local backend is unavailable."); });
    return () => { cancelled = true; };
  }, []);

  const value = useMemo(() => ({
    isAuthenticated: Boolean(principal),
    isLoading: !principal && !error,
    principal,
    capabilities: principal?.capabilities || [],
    hasCapability: capability => Boolean(principal?.capabilities?.includes(capability)),
    getAccessToken: async () => null,
    login: async () => {},
    logout: async () => {},
  }), [principal, error]);

  if (value.isLoading) return <LoadingScreen />;
  if (error) return <SignInScreen error={error} login={() => window.location.reload()} />;
  return <AppAuthContext.Provider value={value}>{children}</AppAuthContext.Provider>;
}

export function AppAuthProvider({ children }) {
  if (AUTH_MODE === "disabled") {
    return <DevelopmentBridge>{children}</DevelopmentBridge>;
  }

  const authority = process.env.REACT_APP_OIDC_AUTHORITY;
  const clientId = process.env.REACT_APP_OIDC_CLIENT_ID;
  const audience = process.env.REACT_APP_OIDC_AUDIENCE;
  if (!authority || !clientId) {
    return (
      <SignInScreen
        error="OIDC frontend configuration is incomplete."
        login={() => {}}
      />
    );
  }

  if (authority.replace(/\/$/, "") === "https://accounts.google.com") {
    return <GoogleIdentityBridge clientId={clientId}>{children}</GoogleIdentityBridge>;
  }

  const redirectUri = window.location.origin;
  const extraQueryParams = audience ? { audience } : undefined;
  return (
    <AuthProvider
      authority={authority}
      client_id={clientId}
      redirect_uri={redirectUri}
      silent_redirect_uri={redirectUri}
      post_logout_redirect_uri={redirectUri}
      response_type="code"
      scope={process.env.REACT_APP_OIDC_SCOPE || "openid profile email"}
      automaticSilentRenew
      monitorSession={false}
      loadUserInfo={false}
      userStore={userStore}
      extraQueryParams={extraQueryParams}
      onSigninCallback={user => {
        const returnTo = user?.state?.returnTo;
        window.history.replaceState({}, document.title, returnTo || "/");
      }}
    >
      <OidcBridge>{children}</OidcBridge>
    </AuthProvider>
  );
}

export function useAppAuth() {
  const value = useContext(AppAuthContext);
  if (!value) throw new Error("useAppAuth must be used inside AppAuthProvider");
  return value;
}

